import { isNil } from 'lodash';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { mapCollectionCriteriaToState } from '@shared/components/filter/state/mapCollectionCriteriaToState';
import { mapStateToCollectionCriteria } from '@shared/components/filter/state/mapStateToCollectionCriteria';
import { ControlsState } from '@shared/components/filter/types';
import { useCountWorkflowEntities } from '@shared/scenes/dashboard/hooks/useCountWorkflowEntities';
import { createCtx } from '@shared/util/context';
import { useChannel, useIsDesktop } from '@shared/util/hooks';

interface BulkUpdateState {
  totalCardCount: number;
  showBulkFilterUpdate: boolean;
  setShowBulkFilterUpdate: (showBulkFilterUpdate: boolean) => void;
  isLoading: boolean;
  showBulkFilterUpdateForm: boolean;
  setShowBulkFilterUpdateForm: (showBulkFilterUpdateForm: boolean) => void;
  controlsState: ControlsState;
  setControlsState: (controlState: ControlsState) => void;
  workspaceName: string;
}

const [useBulkUpdateState, BulkUpdateStateProvider] = createCtx<BulkUpdateState>();
export { useBulkUpdateState };

interface BulkUpdateProviderProps {
  children: ReactNode;
  viewTemplate: MBAPI.ViewTemplate;
  workflowTemplateId: string;
  channelId: number;
}

export function BulkUpdateProvider({
  children,
  viewTemplate,
  workflowTemplateId,
  channelId,
}: BulkUpdateProviderProps) {
  const channel = useChannel({ channelId });
  const [showBulkFilterUpdate, setShowBulkFilterUpdate] = useState(false);
  const [showBulkFilterUpdateForm, setShowBulkFilterUpdateForm] = useState(false);
  const [controlsState, setControlsState] = useState<ControlsState>(
    mapCollectionCriteriaToState(null, [], [], []),
  );
  const isDesktop = useIsDesktop();
  const { filterByComponents } = mapStateToCollectionCriteria(controlsState);
  const { search } = controlsState;

  const input = useMemo(() => {
    return {
      viewTemplateId: viewTemplate.id,
      workflowTemplateId,
      filterByComponents,
      omniSearchQuery: search,
    };
  }, [viewTemplate, filterByComponents, workflowTemplateId, search]);

  const { totalResults: totalCardCount, loading: isLoading } = useCountWorkflowEntities({
    input,
    skip: isNil(viewTemplate) || !showBulkFilterUpdate,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    setShowBulkFilterUpdate(false);
  }, [viewTemplate?.id]);

  return (
    <BulkUpdateStateProvider
      value={{
        showBulkFilterUpdate: isDesktop && showBulkFilterUpdate,
        setShowBulkFilterUpdate,
        showBulkFilterUpdateForm: isDesktop && showBulkFilterUpdateForm,
        setShowBulkFilterUpdateForm,
        totalCardCount,
        isLoading,
        controlsState: controlsState ?? null,
        setControlsState,
        workspaceName: channel?.name ?? '',
      }}
    >
      {children}
    </BulkUpdateStateProvider>
  );
}

export function useBulkUpdateControlState({ controlState }: { controlState?: ControlsState }) {
  const { setControlsState } = useBulkUpdateState();
  useEffect(() => {
    if (isNil(controlState)) {
      return;
    }
    setControlsState(controlState);
  }, [controlState, setControlsState]);
}
